// src/features/analytics/charts.tsx
// Hand-rolled SVG charts for widget/report rendering — no chart library, no
// new deps. Mirrors the approach in features/project/charts.tsx: semantic
// Tailwind token colours (fill-*/stroke-*) so both themes track automatically.
// Kept local to this feature rather than importing project's copy.

import type { AggregationGroup, ColumnDef, DataPoint, Row, TimeSeries } from '@core/types/analytics';

const W = 560;
const H = 220;
const PAD = { top: 16, right: 16, bottom: 32, left: 40 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const SERIES_STROKE = ['stroke-primary', 'stroke-accent', 'stroke-destructive', 'stroke-secondary'];
const SERIES_FILL = ['fill-primary', 'fill-accent', 'fill-muted-foreground', 'fill-destructive', 'fill-secondary'];
const SERIES_SWATCH = ['bg-primary', 'bg-accent', 'bg-muted-foreground', 'bg-destructive', 'bg-secondary'];

function niceMax(v: number): number {
  if (v <= 0) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

function ChartFrame({
  title,
  yMax,
  xLabels,
  children,
}: {
  title: string;
  yMax: number;
  xLabels: string[];
  children: React.ReactNode;
}) {
  const ticks = 4;
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[360px]" role="img" aria-label={title} preserveAspectRatio="xMidYMid meet">
        <title>{title}</title>
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const y = PAD.top + (PLOT_H * i) / ticks;
          const val = Math.round(yMax - (yMax * i) / ticks);
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} className="stroke-border" strokeWidth={1} strokeDasharray={i === ticks ? undefined : '2 3'} />
              <text x={PAD.left - 6} y={y + 3} textAnchor="end" className="fill-muted-foreground" fontSize={10}>
                {val}
              </text>
            </g>
          );
        })}
        {xLabels.map((lbl, i) => {
          const x = xLabels.length === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (PLOT_W * i) / (xLabels.length - 1);
          return (
            <text key={i} x={x} y={H - PAD.bottom + 16} textAnchor="middle" className="fill-muted-foreground" fontSize={10}>
              {lbl}
            </text>
          );
        })}
        {children}
      </svg>
    </div>
  );
}

function Legend({ items }: { items: { label: string; swatch: string }[] }) {
  if (items.length <= 1) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={`inline-block h-2.5 w-2.5 rounded-sm ${it.swatch}`} aria-hidden="true" />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metric — single big number
// ---------------------------------------------------------------------------

export function MetricTile({ label, value, format }: { label: string; value: number; format?: string }) {
  const display =
    format === 'currency'
      ? value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
      : format === 'percentage'
        ? `${value.toFixed(1)}%`
        : value.toLocaleString();
  return (
    <div className="flex flex-col items-start gap-1 p-2">
      <span className="text-3xl font-semibold tabular-nums text-foreground">{display}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line / area — time series
// ---------------------------------------------------------------------------

export function LineAreaChart({ series, title, filled, empty }: { series: TimeSeries[]; title: string; filled?: boolean; empty: string }) {
  const points: DataPoint[] = series[0]?.data_points ?? [];
  if (series.length === 0 || points.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const allValues = series.flatMap((s) => s.data_points.map((p) => p.value));
  const yMax = niceMax(Math.max(1, ...allValues));
  const n = points.length;
  const x = (i: number) => (n === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (PLOT_W * i) / (n - 1));
  const y = (v: number) => PAD.top + PLOT_H - (PLOT_H * Math.max(0, v)) / yMax;
  const xLabels = points.map((p) => {
    const d = new Date(p.timestamp);
    return Number.isNaN(d.getTime()) ? p.timestamp : d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  });

  return (
    <div>
      <ChartFrame title={title} yMax={yMax} xLabels={xLabels}>
        {series.map((s, si) => {
          const line = s.data_points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');
          const area = filled
            ? `${PAD.left},${PAD.top + PLOT_H} ${line} ${x(s.data_points.length - 1)},${PAD.top + PLOT_H}`
            : undefined;
          return (
            <g key={s.metric || si}>
              {area && <polygon points={area} className={`${SERIES_FILL[si % SERIES_FILL.length]} opacity-20`} />}
              <polyline points={line} fill="none" className={SERIES_STROKE[si % SERIES_STROKE.length]} strokeWidth={2} />
            </g>
          );
        })}
      </ChartFrame>
      <Legend items={series.map((s, i) => ({ label: s.label || s.metric, swatch: SERIES_SWATCH[i % SERIES_SWATCH.length] }))} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bar — one bar per aggregation group (also used for funnel/cohort/heatmap/
// scatter/map widget types as a reasonable generic fallback)
// ---------------------------------------------------------------------------

export function BarChart({ groups, valueKey, title, empty }: { groups: AggregationGroup[]; valueKey: string; title: string; empty: string }) {
  if (groups.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const values = groups.map((g) => g.values?.[valueKey] ?? Object.values(g.values ?? {})[0] ?? 0);
  const yMax = niceMax(Math.max(1, ...values));
  const n = groups.length;
  const groupW = PLOT_W / n;
  const barW = Math.min(36, groupW - 8);
  const y = (v: number) => PAD.top + PLOT_H - (PLOT_H * Math.max(0, v)) / yMax;
  const xLabels = groups.map((g) => Object.values(g.key ?? {}).join(' / ') || '—');

  return (
    <ChartFrame title={title} yMax={yMax} xLabels={xLabels}>
      {groups.map((_, i) => {
        const v = values[i];
        const cx = PAD.left + groupW * i + groupW / 2;
        const barH = PAD.top + PLOT_H - y(v);
        return <rect key={i} x={cx - barW / 2} y={y(v)} width={barW} height={barH} rx={2} className="fill-primary" />;
      })}
    </ChartFrame>
  );
}

// ---------------------------------------------------------------------------
// Pie / donut — proportions
// ---------------------------------------------------------------------------

export function PieChart({ groups, valueKey, donut, empty }: { groups: AggregationGroup[]; valueKey: string; donut?: boolean; empty: string }) {
  const values = groups.map((g) => g.values?.[valueKey] ?? Object.values(g.values ?? {})[0] ?? 0);
  const total = values.reduce((s, v) => s + v, 0);
  if (groups.length === 0 || total <= 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const cx = 100;
  const cy = 100;
  const r = 90;
  const inner = donut ? 50 : 0;
  let angle = -Math.PI / 2;

  const slices = values.map((v, i) => {
    const frac = v / total;
    const start = angle;
    const end = angle + frac * 2 * Math.PI;
    angle = end;
    const p1 = { x: cx + r * Math.cos(start), y: cy + r * Math.sin(start) };
    const p2 = { x: cx + r * Math.cos(end), y: cy + r * Math.sin(end) };
    const large = end - start > Math.PI ? 1 : 0;
    const outer = `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 1 ${p2.x} ${p2.y} Z`;
    let d = outer;
    if (inner > 0) {
      const ip1 = { x: cx + inner * Math.cos(start), y: cy + inner * Math.sin(start) };
      const ip2 = { x: cx + inner * Math.cos(end), y: cy + inner * Math.sin(end) };
      d = `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 1 ${p2.x} ${p2.y} L ${ip2.x} ${ip2.y} A ${inner} ${inner} 0 ${large} 0 ${ip1.x} ${ip1.y} Z`;
    }
    return { d, fill: SERIES_FILL[i % SERIES_FILL.length], pct: Math.round(frac * 100) };
  });

  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg viewBox="0 0 200 200" className="h-40 w-40 flex-shrink-0" role="img" aria-label="chart">
        {slices.map((s, i) => (
          <path key={i} d={s.d} className={s.fill} />
        ))}
      </svg>
      <div className="space-y-1">
        {groups.map((g, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${SERIES_SWATCH[i % SERIES_SWATCH.length]}`} aria-hidden="true" />
            <span className="text-foreground">{Object.values(g.key ?? {}).join(' / ') || '—'}</span>
            <span>{slices[i].pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data table — QueryData / QueryResult rendering
// ---------------------------------------------------------------------------

export function ResultTable({ columns, rows, empty }: { columns: ColumnDef[]; rows: Row[]; empty: string }) {
  if (columns.length === 0 || rows.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="table table-compact w-full">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.name} className="text-left text-xs font-medium text-muted-foreground">
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.values.map((v, ci) => (
                <td key={ci} className="text-sm text-foreground">
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aggregation table — AggregationResult rendering (saved query "run" results)
// ---------------------------------------------------------------------------

export function AggregationTable({ groups, empty }: { groups: AggregationGroup[]; empty: string }) {
  if (groups.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const keyFields = Array.from(new Set(groups.flatMap((g) => Object.keys(g.key ?? {}))));
  const valueFields = Array.from(new Set(groups.flatMap((g) => Object.keys(g.values ?? {}))));
  return (
    <div className="overflow-x-auto">
      <table className="table table-compact w-full">
        <thead>
          <tr>
            {keyFields.map((k) => (
              <th key={k} className="text-left text-xs font-medium text-muted-foreground">
                {k}
              </th>
            ))}
            {valueFields.map((v) => (
              <th key={v} className="text-right text-xs font-medium text-muted-foreground">
                {v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, i) => (
            <tr key={i}>
              {keyFields.map((k) => (
                <td key={k} className="text-sm text-foreground">
                  {g.key?.[k] ?? ''}
                </td>
              ))}
              {valueFields.map((v) => (
                <td key={v} className="text-right text-sm tabular-nums text-foreground">
                  {g.values?.[v] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
